import React from 'react';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { usePageVisibility } from '@/hooks/usePageVisibility';

interface BreathingCircleProps {
  variant: 'focus' | 'admin' | 'break';
  isPaused?: boolean;
}

export const BreathingCircle = React.memo(function BreathingCircle({ variant, isPaused }: BreathingCircleProps) {
  const isVisible = usePageVisibility();
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    const handleChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Get the CSS variable name based on variant
  const colorVar = variant === 'break' ? 'success' : variant === 'focus' ? 'primary' : 'secondary';

  // Animation config - matching the reference exactly
  // Pause animations when isPaused, reduced motion preferred, or not visible
  const shouldAnimate = !isPaused && !prefersReducedMotion && isVisible;
  
  const breatheAnimation = shouldAnimate ? {
    scale: [1, 1.5, 1],
    opacity: [0.6, 1, 0.6],
  } : {};

  const breatheTransition = {
    duration: 8,
    repeat: Infinity,
    ease: "easeInOut" as const,
  };

  return (
    <div className="relative w-[120px] h-[120px] flex items-center justify-center">
      {/* Main breathing circle - 80px */}
      <motion.div
        className="absolute w-[80px] h-[80px] rounded-full"
        animate={breatheAnimation}
        transition={breatheTransition}
        style={{
          background: `radial-gradient(circle, hsl(var(--${colorVar}) / 0.3) 0%, hsl(var(--${colorVar}) / 0.05) 100%)`,
        }}
      />

      {/* Inner breathing circle - 60px with 1s delay */}
      <motion.div
        className="absolute w-[60px] h-[60px] rounded-full"
        animate={breatheAnimation}
        transition={{
          ...breatheTransition,
          delay: shouldAnimate ? 1 : 0,
        }}
        style={{
          background: `radial-gradient(circle, hsl(var(--${colorVar}) / 0.2) 0%, transparent 70%)`,
        }}
      />
    </div>
  );
});
